import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {Section} from 'constants/NavConstants';
import {PolymerSearchView} from 'molbio/BioentitySearchView';
import {AddPolymersModalSharedProps} from 'molbio/bulk-assembly/add-polymers/AddPolymersConstants';
import {updateFragmentsData} from 'molbio/bulk-assembly/add-polymers/AddPolymersUtil';
import {IMMERSIVE_MODAL_PROPS} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {FragmentModalFragment} from 'molbio/bulk-assembly/polymer-map-modals/FragmentCardContents';
import FragmentsModalContent from 'molbio/bulk-assembly/polymer-map-modals/FragmentsModalContent';
import {fragmentDataToNormalizedFragment} from 'molbio/bulk-assembly/util/BulkAssemblyUtil';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import {RowSelectionMode} from 'ui-library/scrollable/ScrollableTableConstants';
import {withLoadingScreen} from 'util/Promises';

enum Steps {
  SEARCH = 'SEARCH',
  SELECT_FRAGMENTS = 'SELECT_FRAGMENTS',
}

/**
 * Helper for building the modal buttons that are available on the search step.
 * @param disableNext Whether the Next button should be disabled.
 */
function getSearchButtons(disableNext: boolean): Array<ModalButton> {
  return [
    {text: 'Cancel', cancel: true},
    {
      text: 'Next',
      style: ButtonStyle.SECONDARY,
      submit: true,
      disabled: disableNext,
      dataTestAttrs: {component: 'AddPolymersSearchModal', key: ModalButtonKey.NEXT},
    },
  ];
}

/**
 * Helper for building the modal buttons that are available on the fragment selection step.
 * @param onClickBack Callback to execute when the Back button is clicked.
 */
function getFragmentSelectionButtons(onClickBack: () => void): Array<ModalButton> {
  return [
    {
      text: 'Back',
      style: ButtonStyle.TERTIARY_GRAY,
      onClick: onClickBack,
      disabled: false,
      dataTestAttrs: {component: 'AddPolymersSearchModal', key: ModalButtonKey.BACK},
    },
    {
      text: 'Add',
      style: ButtonStyle.SECONDARY,
      submit: true,
      disabled: false,
      dataTestAttrs: {component: 'AddPolymersSearchModal', key: ModalButtonKey.ADD},
    },
  ];
}

interface MaxNumPolymersProps {
  maxNumEntitiesToAdd: number;
}

export type AddPolymersSearchModalProps = AddPolymersModalSharedProps & MaxNumPolymersProps;

/**
 * Modal component for adding new fragments to the bulk assembly using search.
 *
 * Presents a two-step flow for searching for sequences and then selecting fragments from those sequences.
 * TODO (BNCH-55735): Consolidate shared flow with AddPolymersSearchModal
 * @param assemblyId ID of the assembly. Used for analytics.
 * @param bulkAssemblyMethodData What kind of bulk assembly is being performed, and associated parameters.
 * @param canSpecifyEndpoints Whether the user can specify endpoints for fragments.
 * @param canUpdateRestrictionEnzymes Whether the user can update the restriction enzymes for individual fragments.
 * @param onRequestSubmit Callback for adding the selected fragments to the assembly.
 * @param binColor Color associated with the fragment bin.
 * @param enableSelectPreferredPrimers Whether to enable preferred primer selection during fragment selection.
 * @param binCid Bin id to add the new fragments to.
 * @param binIndex Index of the bin with respect to all fragment bins.
 * @param polymerSubtypes Accepted polymer subtypes to search for.
 * @param onRequestClose Callback executed when the modal is closed or submitted.
 * @param topology The assembly topology
 * @param numFragmentBins Number of fragment bins in the assembly
 * @param fragmentProductionMethod Fragment production method for the bin.
 * @constructor
 */
export default function AddPolymersSearchModal({
  assemblyId,
  bulkAssemblyMethodData,
  canSpecifyEndpoints,
  canUpdateRestrictionEnzymes,
  onRequestSubmit,
  binColor,
  enableSelectPreferredPrimers,
  binCid,
  binIndex,
  constructFragmentIndex,
  maxNumEntitiesToAdd,
  polymerSubtypes,
  onRequestClose,
  constructTopology,
  numConstructFragments,
  fragmentProductionMethod,
}: AddPolymersSearchModalProps): JSX.Element {
  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
  const [currentStep, setStep] = useState<Steps>(Steps.SEARCH);
  const [fragmentsData, setFragmentsData] = useState<Array<FragmentModalFragment>>([]);

  const onClickNext = async (): Promise<void> => {
    await withLoadingScreen(
      updateFragmentsData(selectedIds, fragmentsData, setFragmentsData, {
        polymerSubtypes,
        enableSelectPreferredPrimers,
        binCid,
        binColor,
        binIndex,
        constructFragmentIndex,
        canSpecifyEndpoints,
        canUpdateRestrictionEnzymes,
        constructTopology,
        numConstructFragments,
        bulkAssemblyMethodData,
        fragmentProductionMethod,
      })
    );
    setStep(Steps.SELECT_FRAGMENTS);
  };

  const onClickBack = (): void => {
    setStep(Steps.SEARCH);
  };

  const buttons: Array<ModalButton> =
    currentStep === Steps.SEARCH
      ? getSearchButtons(selectedIds.length === 0 || selectedIds.length > maxNumEntitiesToAdd)
      : getFragmentSelectionButtons(onClickBack);

  return (
    <Modal
      {...IMMERSIVE_MODAL_PROPS}
      title={currentStep === Steps.SEARCH ? 'Search for sequences' : 'Add fragment(s)'}
      buttons={buttons}
      onRequestClose={() => {
        onRequestClose();
      }}
      onRequestSubmit={async () => {
        if (currentStep === Steps.SEARCH) {
          await onClickNext();
        } else {
          onRequestSubmit(
            fragmentsData.map((fragmentData) => fragmentDataToNormalizedFragment(fragmentData))
          );
        }
      }}
    >
      {currentStep === Steps.SEARCH ? (
        <div className={css(styles.sequenceSearchView)}>
          <PolymerSearchView
            limitSelectedEntitiesParams={{maxNumEntitiesToAdd, selectedEntityName: 'fragments'}}
            navSection={Section.MOLBIO_BULK_ASSEMBLY}
            polymerSubtypes={polymerSubtypes}
            selectedBioentityIds={selectedIds}
            setSelectedBioentityIds={setSelectedIds}
            rowSelectionMode={RowSelectionMode.MULTIPLE}
          />
        </div>
      ) : (
        <FragmentsModalContent
          assemblyId={assemblyId}
          bulkAssemblyMethod={bulkAssemblyMethodData}
          canSpecifyEndpoints={canSpecifyEndpoints}
          fragmentsData={fragmentsData}
          setFragmentsData={setFragmentsData}
          isReadOnly={false}
          constructTopology={constructTopology}
          numConstructFragments={numConstructFragments}
        />
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  sequenceSearchView: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
});
